import getState, { createPromise, formatNumber, generateGUID } from "../utils/MainHelper";
import { fGetBehaviorMatchAndColor, getAllKeywordsInFolder, getBrandGenericsHcp } from "../utils/oneintelligence/OneIntelligenceHelper";
import MainServices from "./Main.services";
import MinifiedServices from "./Minified.services";
import ReportsServices from "./Reports.services";
import TopLevelCacheServices from "./TopLevelCache.services";
import TrackFeatureServices from "./TrackFeature.services";
import WhiteSpaceServices from "./WhiteSpace.services";
import WorkerServices from "./Worker.services";

class InsightsServices {
    constructor() {
        this.sqs = "https://sqs.ap-southeast-1.amazonaws.com/788016873502/oneintelligence-cgpt";
        this.languages = {};
    }

    getLanguagesPerCountry() {
        const state = getState();
        const { country } = state.buildTargets.setup;
        try {
            return this.languages[country.name] || [];
        } catch (error) {
            return [];
        }
    }

    loadLanguages() {
        if (!Object.keys(this.languages).length) {
            return MainServices.getFile("oneintel/config/languagelist.json").then((resp) => {
                this.languages = resp;
            });
        }
        return Promise.resolve();
    }

    async runRequest(filename, requestBody) {
        const { promise, resolve } = createPromise();
        const getFile = () => {
            MainServices.getFile(filename, "cgpt").then((resp) => {
                if (!resp) {
                    setTimeout(() => {
                        getFile();
                    }, 1000);
                    return;
                }
                if (resp.status === "error") {
                    console.log("Error", resp.data);
                    resolve("error");
                    return;
                }
                if (resp.status !== "done") {
                    setTimeout(() => {
                        getFile();
                    }, 1000);
                } else {
                    resolve(resp);
                }
            });
        };
        const cache = await WorkerServices.checkIfHasCache([requestBody, ["filename"]]);
        if (cache !== "nocache") return Promise.resolve(cache)


        MainServices.sqsSendMessage(this.sqs, JSON.stringify(requestBody)).then(() => {
            setTimeout(() => {
                getFile();
            }, 3000);
        });

        return promise.then((resp) => {
            WorkerServices.setCache([resp, requestBody, ["filename"]]);
            return resp
        })
    }

    async runIndividual(data, language) {
        const state = getState();
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()

        if (data.fromMinified) {
            const cacheResult = await TopLevelCacheServices.getMinifiedInsightsCache(data.item);
            if (cacheResult) return cacheResult
        }
        const { trackName, trackKey } = state.reports.visibleInsightsModal;
        const { country } = state.buildTargets.setup;
        const {
            mastersName, item, behaviormatch, score, categoryName
        } = data;
        const filename = `individual~${account}~${username}~${generateGUID()}.json`;
        const requestBody = {
            username,
            account,
            filename,
            function: "individual",
            data: {
                country: country.name === "Global Medical" ? "Global" : country.name,
                master: mastersName,
                category: categoryName,
                behavior: item,
                score: formatNumber(score * 100, 2),
                interest: behaviormatch,
                language
            }
        };

        TrackFeatureServices.trackFeature(trackName, requestBody.function, requestBody)

        const { startMonitor, endMonitor } = TrackFeatureServices.getMonitor({ featureKey: trackKey })
        const url = this.sqs + "/" + requestBody.function
        startMonitor(requestBody, { url })
        return this.runRequest(filename, requestBody).then((resp) => {
            if (data.fromMinified) {
                TopLevelCacheServices.setMinifiedInsightsCache(resp, data.item);
            }
            endMonitor(resp, { url })
            return resp
        })
    }

    runCompare(data1, data2, language) {
        const state = getState();
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const { country } = state.buildTargets.setup;
        const { mastersName } = data1;
        const filename = `compare~${account}~${username}~${generateGUID()}.json`;
        const requestBody = {
            username,
            account,
            filename,
            function: "compare",
            data: {
                country: country.name === "Global Medical" ? "Global" : country.name,
                master: mastersName,
                compare1: {
                    category: data1.categoryName,
                    behavior: data1.item,
                    score: formatNumber(data1.score * 100, 2),
                    interest: data1.behaviormatch
                },
                compare2: {
                    category: data2.categoryName,
                    behavior: data2.item,
                    score: formatNumber(data2.score * 100, 2),
                    interest: data2.behaviormatch
                },
                language
            }
        };

        const { startMonitor, endMonitor } = TrackFeatureServices.getMonitor({ featureKey: "advanceRunCompare" })
        const url = this.sqs + "/" + requestBody.function
        startMonitor(requestBody, { url })
        TrackFeatureServices.trackFeature("compare", requestBody.function, requestBody)
        return this.runRequest(filename, requestBody).then((resp) => {
            endMonitor(resp, { url })
            return resp
        })
    }

    runSimpleSummary(targets, categories, track, type, callBackGetUrl = () => { }) {
        const state = getState();
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const { country } = state.buildTargets.setup;

        const newCategories = categories.reduce((acc, cat) => {
            const averageObj = cat.children.reduce((aveobj, child) => {
                if (child.score > .3) {
                    aveobj.sum += child.score
                    aveobj.count += 1
                }
                return aveobj
            }, { sum: 0, count: 0 })
            const averageinterest = averageObj.sum / averageObj.count;
            if (averageinterest > .3) {
                acc.push({
                    "category": cat.discoveredCategory,
                    "avginterest": formatNumber(averageinterest * 100, 2),
                    "targetsize": (cat.size).toString(),
                    "behavior": fGetBehaviorMatchAndColor(averageinterest).behaviormatch
                })
            }
            return acc
        }, [])

        const filename = `simplesummary~${account}~${username}~${generateGUID()}.json`;
        const requestBody = {
            username,
            account,
            filename,
            function: "simple summary",
            data: {
                country: country.name === "Global Medical" ? "Global" : country.name,
                master: targets.join(","),
                data: newCategories
            }
        }

        callBackGetUrl(requestBody, this.sqs + "/" + requestBody.function)
        return TopLevelCacheServices.getMinifiedRecTextCache(type).then((cacheResp) => {
            if (track) {
                TrackFeatureServices.trackFeature("minified package insights", requestBody.function, requestBody)
            }
            if (cacheResp) {
                return cacheResp
            }
            return this.runRequest(filename, requestBody).then(async (resp) => {
                await TopLevelCacheServices.setMinifiedRecTextCache(resp, type)
                return resp
            })
        })
    }

    getKeywordSuggestion(givenTarget) {
        const state = getState();
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const { targets } = state.minified;
        const filename = `kwsuggestion~${account}~${username}~${generateGUID()}.json`;
        const requestBody = {
            username,
            account,
            filename,
            function: "keyword suggestion",
            data: {
                master: (givenTarget || targets).join(", "),
            }
        }

        return this.runRequest(filename, requestBody)
    }

    getTargetsFromText(text, callBackStart = () => { }) {
        const options = {
            gateway: {
                class: "oneintel",
                function: "getentities"
            },
            method: "GET",
            _isQuery: true,
            _withData: false,
            _exclude: [
                "_class",
                "_function",
                "_account",
                "_username",
            ]
        };
        const params = { data: text }
        const callBackGetUrl = (url) => {
            callBackStart(params, url)
        }
        const prm = MainServices.get({ path: "oneintel/getentities", params, options: { noCredentials: true }, callBackGetUrl })
        return prm.then((data) => {
            TrackFeatureServices.trackFeature("minified", "getentities", {
                text,
                result: data
            })
            return data
        })
    }

    identifyPositiveNegative(text) {
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const filename = `indentifynegpos~${account}~${username}~${generateGUID()}.json`;
        const requestBody = {
            username,
            account,
            filename,
            function: "identify positive negative",
            data: text
        }
        return this.runRequest(filename, requestBody)
    }

    get10Keywords(list) {
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const filename = `whitespace10keywords~${account}~${username}~${generateGUID()}.json`;

        const { promise, resolve } = createPromise();
        // const givenMaster = list.reduce((acc, item) => {
        //     const itemTree = WhiteSpaceServices.findItemInTree(item)
        //     const childName = itemTree.name.toLocaleLowerCase();
        //     const childList = itemTree.children || itemTree.tempChildren;
        //     const keywords = childList ? getAllKeywordsInFolder(childList, []) : [childName]
        //     acc[itemTree.title.toLocaleLowerCase()] = keywords;
        //     return acc
        // }, {})
        const givenMaster = ReportsServices.getMasters(true);

        const requestBody = {
            username,
            account,
            filename,
            function: "10 Keywords",
            data: {
                keywords: list.reduce((acc, t) => {
                    (t.namePath || t.path).split("/").forEach((text) => {
                        if (text.split("~~").length > 1) {
                            acc.push(text.split("~~")[1].toLocaleLowerCase());
                        } else {
                            acc.push(text.toLocaleLowerCase());
                        }
                    })
                    return acc
                }, [])
            }
        }
        const forceSqs = "https://sqs.ap-southeast-1.amazonaws.com/788016873502/oi-persona-main";

        MinifiedServices.runPersonas(filename, requestBody, undefined, undefined, forceSqs).then((res) => {
            try {
                const tenKeywords = res?.resp?.data.topics || Object.values(res?.resp?.data || {}) || []
                const initialCallBack = (obj) => { }
                const doneApiCallBack = (obj, children) => {
                    resolve(children)
                }
                const { commonParams } = TrackFeatureServices.getMonitor({ featureKey: "whitespaceDeepdive10Keywords" })
                ReportsServices.getFreeFormExact(false, initialCallBack, doneApiCallBack, undefined, commonParams, { givenMaster, givenSelections: tenKeywords, noNotif: true })
            } catch (error) {
                resolve([]);
            }
            return res
        })

        return promise
    }

    getTopWhitespaceBrandspaceText(givenList, fnc = "whitespace") {
        const state = getState();
        const { username } = MainServices.userDetails()
        const account = MainServices.getAccount()
        const { whitespaceSummary: {
            topWhitespace,
            topBrandSpace
        } } = state.reports;
        const { country } = state.buildTargets.setup;
        const filename = `${fnc}toptext~${account}~${username}~${generateGUID()}.json`;

        const {
            hcps,
            brands,
            generics
        } = getBrandGenericsHcp();

        let master = ""
        const hcpsKeys = Object.keys(hcps)
        const bransKeys = Object.keys(brands)
        if (hcpsKeys.length) {
            master = `${hcpsKeys.join(", ")} interested in ${bransKeys.join(", ")}`
        } else {
            master = `${generics.join(", ")} and ${bransKeys.join(", ")}`
        }
        const getList = givenList || (fnc === "whitespace" ? topWhitespace : topBrandSpace)
        const requestBody = {
            username,
            account,
            filename,
            function: fnc,
            data: {
                country: country.name === "Global Medical" ? "Global" : country.name,
                master,
                data: getList.reduce((acc, item) => {
                    acc[item.name] = (item.newFormulaScore || item.diff).toFixed(2)
                    return acc
                }, {})
            }
        };

        return this.runRequest(filename, requestBody);
    }
}

export default new InsightsServices();
